import { GROUP_MEMBER_MAX } from "@rakazo/contracts";

type DraggedChat = {
  kind: "bot" | "group";
  id: string;
  sectionId: string | null;
  pinned: boolean;
};

export type RosterDropTarget =
  | { kind: "bot"; chat: { id: string; sectionId: string | null; pinned: boolean } }
  | {
      kind: "group";
      chat: {
        id: string;
        sectionId: string | null;
        pinned: boolean;
        memberIds: readonly string[];
      };
    }
  | { kind: "section"; sectionId: string | null }
  | { kind: "pinned" };

export type RosterDropAction =
  | { action: "reorder"; targetBotId: string }
  | { action: "move"; sectionId: string | null }
  | { action: "move-group"; sectionId: string | null }
  | { action: "pin-group" }
  | { action: "join"; groupId: string; botIds: string[] };

/**
 * Decide what dropping a dragged sidebar row does. A bot dropped on a sibling
 * reorders; a bot dropped on another section moves there; a bot dropped on a
 * group chat joins it. A group chat dropped on a section or one of its rows
 * moves there, and a group chat dropped on Pinned is pinned. Pinned rows only
 * reorder, so a pinned chat is not dragged into a section. Returns null when
 * the drop should be refused.
 */
export function resolveRosterDrop(input: {
  dragged: DraggedChat;
  target: RosterDropTarget;
  /** True when the target and dragged rows belong to the same visible list. */
  sameList: boolean;
}): RosterDropAction | null {
  const { dragged, target } = input;
  if (dragged.kind === "group") return resolveGroupDrop(dragged, target);
  switch (target.kind) {
    case "bot": {
      if (target.chat.id === dragged.id) return null;
      if (dragged.pinned !== target.chat.pinned) return null;
      if (dragged.pinned || target.chat.sectionId === dragged.sectionId) {
        return input.sameList ? { action: "reorder", targetBotId: target.chat.id } : null;
      }
      return { action: "move", sectionId: target.chat.sectionId };
    }
    case "section": {
      if (dragged.pinned || target.sectionId === dragged.sectionId) return null;
      return { action: "move", sectionId: target.sectionId };
    }
    case "pinned":
      return null;
    case "group": {
      const members = target.chat.memberIds;
      if (members.includes(dragged.id) || members.length >= GROUP_MEMBER_MAX) return null;
      return { action: "join", groupId: target.chat.id, botIds: [...members, dragged.id] };
    }
  }
}

function resolveGroupDrop(dragged: DraggedChat, target: RosterDropTarget): RosterDropAction | null {
  switch (target.kind) {
    case "pinned":
      return dragged.pinned ? null : { action: "pin-group" };
    case "section":
      return moveGroup(dragged, target.sectionId);
    case "bot":
      if (!dragged.pinned && target.chat.pinned) return { action: "pin-group" };
      return moveGroup(dragged, target.chat.sectionId);
    case "group": {
      if (target.chat.id === dragged.id) return null;
      if (!dragged.pinned && target.chat.pinned) return { action: "pin-group" };
      return moveGroup(dragged, target.chat.sectionId);
    }
  }
}

function moveGroup(dragged: DraggedChat, sectionId: string | null): RosterDropAction | null {
  if (dragged.pinned || sectionId === dragged.sectionId) return null;
  return { action: "move-group", sectionId };
}
